import React, { useContext, useEffect, useState } from 'react'
import GuiPieChart from './GuiPieChart'
import { Box } from '@mui/material'
import { WallboxHardwareModel } from 'src/Libs/wallboxes/models'
import { LiveDataModel, PowermeterHardwareModel } from 'src/State/Storage/StorageModel'
import GuiLiveViewItem from './GuiLiveViewItem'
import { useGuiLiveValueItems, useResponsiveHelpers } from 'src/Hooks'
import { DASHBOARD_LIVE_VIEW_ID } from 'src/Libs/qa/ids'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { useMediaQuery, useTheme } from '@mui/system'
import useCheckDynamicPowerPriceApplicability from 'src/Libs/dynamic-power-prices/hooks/use-check-dynamic-power-price-applicability'
import { useFarmContext } from 'src/Elements/Farm'
import { GUI_LIVE_VIEW_BREAKPOINT_360, GUI_LIVE_VIEW_BREAKPOINT_XS } from 'src/Libs/live-view/utils/breakpoints'
import { isFlowTheme } from 'src/Libs/utils'
import Lottie from 'lottie-react'
import ai360Animation from 'src/Libs/live-view/assets/ai360-animation.json'
import { makeStyles } from 'tss-react/mui'
import { useDynamicEms, useIsDynamicEmsAccessible } from 'src/Libs/dynamic-power-prices'
import { useParams } from 'react-router-dom'

const useStyles = makeStyles()(theme => ({
  guiPieChartContainer: {
    position: 'relative',
    gridRow: 2,
    gridColumn: '1 / span 3',
    justifySelf: 'center',
    alignSelf: 'center',
    marginTop: '-6px' /*-6px because of 6px recharts offset in svg*/,

    [theme.breakpoints.up('sm')]: {
      gridColumn: 2,
    },
  },
  lottieAnimation: {
    position: 'absolute',
    width: 270,
    left: '-66px',
    top: '-64px',

    [theme.breakpoints.down('sm')]: {
      width: 255,
      left: '-63px',
      top: '-60px',
    },
  },
}))

export interface Props {
  storageId: string | undefined
  pvHomePower: number
  homeBatteryPower: number
  trackerHomePower: number
  batteryHomePower: number
  homeWallboxPower: number
  homeConsumerPower: number
  consumption: number
  networkHomePower: number
  homeNetworkPower: number
  socCurrent?: number
  wallboxes?: WallboxHardwareModel[]
  consumers?: PowermeterHardwareModel[]
  producers?: PowermeterHardwareModel[]
  liveData?: LiveDataModel
  shallowLiveView?: boolean
  offlineIcon?: React.ReactNode
  isBlockingTimeActive?: boolean
  showDynamicPrices?: boolean
  isDynamicPowerPriceEnabled?: boolean
}

/**
 * Live View like shown in the Storage GUI
 */
const GuiLiveView: React.FC<Props> = ({
  storageId,
  pvHomePower,
  homeBatteryPower,
  trackerHomePower,
  batteryHomePower,
  homeWallboxPower,
  homeConsumerPower,
  consumption,
  networkHomePower,
  homeNetworkPower,
  wallboxes,
  consumers,
  producers,
  liveData,
  shallowLiveView,
  offlineIcon,
  isBlockingTimeActive = false,
  showDynamicPrices = false,
  isDynamicPowerPriceEnabled = false,
  socCurrent = 0,
}) => {
  const valueVariant = 'h5'
  const { isFarm, farmMaster } = useFarmContext()
  const { classes } = useStyles()
  const { offlineClientStorageIds } = useContext(InstallationOnlineStatusContext)
  const theme = useTheme()
  const upXsLiveView = useMediaQuery(theme.breakpoints.up(GUI_LIVE_VIEW_BREAKPOINT_XS))
  const between360AndXsLiveView = useMediaQuery(theme.breakpoints.between(GUI_LIVE_VIEW_BREAKPOINT_360, GUI_LIVE_VIEW_BREAKPOINT_XS))
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const upSm = useMediaQuery(theme.breakpoints.up('sm'))
  const storageIdToUseForDpp = isFarm && farmMaster ? farmMaster.storageId : storageId
  const { isDynamicPowerPriceEnabled: isDynamicPowerPriceEnabledForFarm } = useCheckDynamicPowerPriceApplicability(storageIdToUseForDpp ?? '')
  const storageIdToUse = isDynamicPowerPriceEnabledForFarm ? storageIdToUseForDpp : storageId
  const hasSecondLevelIcons = Boolean(consumers?.length)

  const getResponsiveValues = useResponsiveHelpers()
  const isMinimal = (!wallboxes || wallboxes?.length === 0) && (!producers || producers?.length === 0)

  const [isAI360Active, setIsAI360Active] = useState(false)
  const { installationId } = useParams<{ installationId: string }>()
  const { data: dynamicEmsSettings } = useDynamicEms(installationId, !!installationId)
  const { isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()

  useEffect(() => {
    if (dynamicEmsSettings) {
      setIsAI360Active(dynamicEmsSettings.isActive)
    } else {
      // "Dynamic EMS not activated" state is treated as inactive AI360, so we set it to false to show the correct UI. Once the user activates Dynamic EMS, the correct state will be reflected in the UI.
      setIsAI360Active(false)
    }
  }, [dynamicEmsSettings])

  const { items, subLevelItems } = useGuiLiveValueItems({
    batteryHomePower,
    homeBatteryPower,
    pvHomePower,
    networkHomePower,
    homeNetworkPower,
    consumption,
    homeConsumerPower,
    homeWallboxPower,
    consumers,
    liveData,
    offlineClientStorageIds,
    offlineIcon,
    producers,
    trackerHomePower,
    shallowLiveView,
    wallboxes,
    isDynamicPowerPriceEnabled,
    isBlockingTimeActive,
    showDynamicPrices,
    storageId,
    socCurrent,
  })

  const renderSubItems = (): React.ReactNode => {
    return subLevelItems.map((subItem, currentIndex) => {
      return (
        <GuiLiveViewItem
          id={`${subItem.label}_${currentIndex}`}
          labelVariant={'body2'}
          valueVariant={valueVariant}
          key={`${subItem.label}_${currentIndex}`}
          showDynamicPrices={isDynamicPowerPriceEnabled}
          isLastRow={!upMd || subItem.position === 'sub-first'}
          {...subItem}
        />
      )
    })
  }

  const getTemplateColumns = (): string => {
    if (upMd && hasSecondLevelIcons) {
      return 'auto 130px auto auto'
    }
    if (between360AndXsLiveView) {
      return 'minmax(60px, 1fr) 130px minmax(60px, 1fr)'
    }
    if (upXsLiveView) {
      return 'minmax(90px, auto) 130px minmax(90px, auto)'
    }
    if (isMinimal) {
      return '1fr minmax(60px, auto) 1fr'
    }

    return '1fr min-content 1fr'
  }

  const showAI360V2Animation = isDynamicEmsV2Accessible && isAI360Active && !isFlowTheme

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }} id="gui-live-view-container">
      <Box
        id="gui-live-view-grid"
        sx={{
          display: 'grid',
          gridTemplateColumns: getTemplateColumns(),
          gridTemplateRows: upMd || !hasSecondLevelIcons ? '1fr 1fr 1fr' : '1fr 1fr 1fr 1fr',
          position: 'relative',
          width: '100%',
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          rowGap: 0,
        }}
        data-qa={DASHBOARD_LIVE_VIEW_ID}
      >
        {showAI360V2Animation ? (
          <Box className={classes.guiPieChartContainer}>
            <Lottie animationData={ai360Animation} className={classes.lottieAnimation} rendererSettings={{ preserveAspectRatio: 'xMidYMid slice' }} />
            <GuiPieChart
              storageId={storageIdToUse}
              isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabledForFarm}
              showAI360V2Animation={showAI360V2Animation}
            />
          </Box>
        ) : (
          <GuiPieChart
            storageId={storageIdToUse}
            isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabledForFarm}
            sx={{ gridRow: 2, gridColumn: upSm ? 2 : '1 / span 3', justifySelf: 'center', alignSelf: 'center', marginTop: '-6px' }} // -6px because of 6px recharts offset in svg
            showAI360V2Animation={showAI360V2Animation}
          />
        )}
        {items.map((item, currentIndex) => {
          return (
            <GuiLiveViewItem
              labelVariant={'body2'}
              valueVariant={valueVariant}
              key={`${item.label}_${currentIndex}`}
              showDynamicPrices={isDynamicPowerPriceEnabled}
              isLastRow={(upMd || !hasSecondLevelIcons) && item.position.includes('bottom')}
              id={`${item.label}_${currentIndex}`}
              {...item}
            />
          )
        })}
        {subLevelItems && !upMd && (
          <Box
            id="gui-live-view-sub-level-items"
            sx={{
              position: 'relative',
              gridRow: upMd ? 3 : 4,
              gridColumn: getResponsiveValues('1 / span 3', '1 / span 2', '1 / span 2'),
              display: 'flex',
              gap: 2,
              justifyContent: getResponsiveValues('flex-end', 'flex-end', 'flex-end'),
              alignItems: 'center',
              right: getResponsiveValues(0, -89, -97),
              top: getResponsiveValues(-32, -20, -24, 0, -15),
            }}
          >
            {renderSubItems()}
          </Box>
        )}
        {subLevelItems && upMd && renderSubItems()}
      </Box>
    </Box>
  )
}

export default GuiLiveView
